// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Resources } from '@shared/localizations/Resources';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
import TerceraRangeControlTemplate from '@front/templates/TerceraRangeControlTemplate.html';
import { ContainerControl } from './ContainerControl';
import { TimeInterval } from '@shared/commons/cache/TimeInterval';
export class TerceraRangeControl extends ContainerControl {
    constructor() {
        super();
        this.objStartValue = null;
        this.objEndValue = null;
    }
    oninit() {
        super.oninit();
        this.RangeValueChanged = new CustomEvent();
        this.on('onMouseDownMain', this.onMouseDownMain);
        this.on('onMouseDownStart', this.onMouseDownStart);
        this.on('onMouseDownEnd', this.onMouseDownEnd);
        this.on('onMouseUpStart', this.onMouseUp);
        this.on('onMouseUpEnd', this.onMouseUp);
        this.observe('valueClockStart valueClockEnd', this.onRangeValueChanged);
        this.ReverseStyle();
        this.TriangleCorrection();
        this.ClockCorrection();
        this.setValue();
    }
    onRangeValueChanged() {
        if (isNullOrUndefined(this.objStartValue)) {
            return;
        }
        if (isNullOrUndefined(this.objEndValue)) {
            return;
        }
        const rangeControlValue = new TimeInterval();
        rangeControlValue.startHours = this.objStartValue.Hours;
        rangeControlValue.startMins = this.objStartValue.Mins;
        rangeControlValue.endHours = this.objEndValue.Hours;
        rangeControlValue.endMins = this.objEndValue.Mins;
        this.RangeValueChanged.Raise(this, rangeControlValue);
    }
    setInitValue(propValue) {
        if (isNullOrUndefined(propValue)) {
            return;
        }
        const start = this.getInitPosition(propValue.startHours, propValue.startMins);
        const end = this.getInitPosition(propValue.endHours, propValue.endMins);
        if (isNaN(start) || isNaN(end)) {
            return;
        }
        void this.set('slid_position_start', start);
        void this.set('slid_position_end', end);
        this.ReverseStyle();
        this.TriangleCorrection();
        this.ClockCorrection();
        this.setValue();
    }
    onMouseDownMain(ev) {
        if (!ev.original.notSkip) {
            return;
        }
        if (!this.MovingNow) {
            ControlsUtils.Capture.SetCapture(this, ev.original);
        }
        this.MovingNow = true;
    }
    onMouseDownStart(ev) {
        this.PressedElement = TerceraRangeControl.START;
        this.switchFocus(true);
        this.onMouseDown(ev);
    }
    onMouseDownEnd(ev) {
        this.PressedElement = TerceraRangeControl.END;
        this.switchFocus(false);
        this.onMouseDown(ev);
    }
    switchFocus(focus) {
        void this.set('focusedStart', focus);
        void this.set('focusedEnd', !focus);
    }
    onMouseDown(ev) {
        const event = new Event('mousedown');
        const variableName = 'slid_position_' + this.PressedElement;
        this.MovingNow = false;
        const evO = ev.original;
        event.pageX = evO.pageX;
        event.offsetX = this.get(variableName);
        event.pageY = evO.pageY;
        event.offsetY = evO.offsetY;
        event.notSkip = true;
        ev.original.currentTarget.parentElement.dispatchEvent(event);
    }
    onMouseUp(ev) {
        this.MovingNow = false;
    }
    onMouseMove(ev) {
        if (!this.MovingNow) {
            return;
        }
        this.processEVT(ev);
    }
    processEVT(ev) {
        if (this.captured && ev.original) {
            return;
        }
        const variableName = 'slid_position_' + this.PressedElement;
        let oldV = this.get(variableName);
        let newV = ev.offsetX;
        if (newV <= 0) {
            newV = 0;
        }
        if (newV >= TerceraRangeControl.RIGHT_BORDER) {
            newV = TerceraRangeControl.RIGHT_BORDER;
        }
        if (oldV !== 0 &&
            oldV !== TerceraRangeControl.RIGHT_BORDER &&
            newV !== 0 &&
            newV !== TerceraRangeControl.RIGHT_BORDER) {
            oldV = newV;
        }
        void this.set(variableName, newV);
        if (oldV !== newV) {
            this.TriangleCorrection();
        }
        this.ReverseStyle();
        this.ClockCorrection();
        this.setValue();
    }
    TriangleCorrection() {
        const positionStart = this.get('slid_position_start');
        const positionEnd = this.get('slid_position_end');
        const arrElem = [TerceraRangeControl.START, TerceraRangeControl.END];
        const arrNum = [positionStart, positionEnd];
        for (let i = 0; i < arrElem.length; i++) {
            const elem = arrElem[i];
            const num = arrNum[i];
            const variableName = 'triangleType_' + elem;
            if (num > 0 && num < TerceraRangeControl.RIGHT_BORDER) {
                void this.set(variableName, 'down');
            }
            if (num === 0) {
                void this.set(variableName, 'left');
            }
            if (num === TerceraRangeControl.RIGHT_BORDER) {
                void this.set(variableName, 'right');
            }
        }
    }
    ClockCorrection() {
        const revers = this.get('isRevers');
        const positionStart = revers ? this.get('slid_position_end') : this.get('slid_position_start');
        const positionEnd = revers ? this.get('slid_position_start') : this.get('slid_position_end');
        const typeStartClock = revers ? 'clock_position_end' : 'clock_position_start';
        const typeEndClock = revers ? 'clock_position_start' : 'clock_position_end';
        this.CalculatePositionClock(positionStart, typeStartClock);
        this.CalculatePositionClock(positionEnd, typeEndClock);
    }
    CalculatePositionClock(position, typeClock) {
        const edgeLeft = 9;
        const edgeRight = 188;
        const halfClock = -9;
        const positionClock = TerceraRangeControl.RIGHT_BORDER - position;
        const indent = 2 * halfClock + positionClock;
        if (position < edgeLeft) {
            void this.set(typeClock, -position);
        }
        else if (position > edgeRight) {
            void this.set(typeClock, indent);
        }
        else {
            void this.set(typeClock, halfClock);
        }
    }
    ReverseStyle() {
        const posStart = this.get('slid_position_start');
        const posEnd = this.get('slid_position_end');
        let isRevers = false;
        if (posStart >= posEnd) {
            isRevers = true;
        }
        void this.set('isRevers', isRevers);
    }
    setValue() {
        const posStart = this.get('slid_position_start');
        const posEnd = this.get('slid_position_end');
        this.objStartValue = this.getFormattedValue(posStart);
        this.objEndValue = this.getFormattedValue(posEnd);
        if (!isNullOrUndefined(this.objStartValue)) {
            void this.set('valueClockStart', this.objStartValue.FormatedValue);
        }
        if (!isNullOrUndefined(this.objEndValue)) {
            void this.set('valueClockEnd', this.objEndValue.FormatedValue);
        }
    }
    getFormattedValue(left) {
        let mins = 0;
        let hours = 0;
        /* кусок черной магии
    9 отступ слева для расчёта по 3 пикселя на 15 минут
    196 отступ справа для расчёта до этого значения по 2 пикселя на 15 минут
    197 граница после которой время ставим 23:59
    всё из-за гавна с размером в 198 пикселей
    192 пикселя было б топычем! и без магии */
        if (left < 9 && left % 3 === 0) {
            mins = ((left / 3) % 4) * 15;
            hours = Math.trunc(left / 3 / 4);
        }
        else if (left >= 9 && left < 196 && left % 2 === 0) {
            mins = ((left / 2) % 4) * 15 + 30;
            if (mins >= 60) {
                mins = mins - 60;
                hours = 1;
            }
            hours += Math.trunc(left / 2 / 4) - 1;
        }
        else if (left > 197) {
            mins = 59;
            hours = 23;
        }
        else {
            return null;
        }
        const objValue = {};
        objValue.Hours = hours;
        objValue.Mins = mins;
        objValue.FormatedValue = this.FormatClockValue(hours, mins);
        return objValue;
    }
    getInitPosition(hours, mins) {
        // еще один кусок черной магии
        let plus = mins / 15;
        if ((hours === 0 && mins > 30) || hours !== 0) {
            plus = 4;
        }
        let res = (hours * 4 + mins / 15) * 2 + plus;
        if (hours === 23 && mins === 59) {
            res = 198;
        }
        return res;
    }
    FormatClockValue(hours, mins) {
        let hoursStr = hours.toString();
        if (hours < 10) {
            hoursStr = '0' + hours;
        }
        let minsStr = mins.toString();
        if (!mins) {
            minsStr = mins + '0';
        }
        const resultValue = hoursStr + ':' + minsStr;
        return resultValue;
    }
}
TerceraRangeControl.RIGHT_BORDER = 198;
TerceraRangeControl.START = 'start';
TerceraRangeControl.END = 'end';
ContainerControl.extendWith(TerceraRangeControl, {
    data: function () {
        return {
            slid_position_start: 0,
            slid_position_end: 198,
            triangleType_start: '',
            triangleType_end: '',
            isRevers: false,
            focusedStart: false,
            focusedEnd: false,
            startDayTooltip: Resources.getResource('RangeControl.StartDaySlide.Tooltip'),
            endDayTooltip: Resources.getResource('RangeControl.EndDaySlide.Tooltip')
        };
    },
    template: TerceraRangeControlTemplate
});
